/**
 * lib/clockingExport.ts
 * ------------------------------------------------------------------
 * HR DOMAIN — the Clocking sheet as paper and as a spreadsheet (Nov 2026).
 *
 * What each day says when it leaves the screen, and how a month is divided to
 * fit A4. Pure; lib/clockingPdf draws the result and the CSV goes through
 * lib/tableExport, as every other screen's does.
 *
 * WORDS AS WELL AS SHADES. Most printing here is black and white, so a late day
 * says how late, and a missing punch says which one. The shade only helps.
 *
 * A MONTH DOES NOT FIT ON A4 at a readable size: 27 days across a page leaves
 * under 8 mm a day. So the school chose to offer both, recommending two halves
 * (1st to 15th, then the rest with the month's totals) over all on one page.
 * ------------------------------------------------------------------ */
import type { PeriodSummary } from '@/lib/clockingRules';
import type { CellKind, RegisterCell } from '@/lib/clockingRegister';
import { fmtMinutes } from '@/lib/clockingRules';
import { shortLate } from '@/lib/clockingRegister';
import { weekday } from '@/lib/clockingRules';
import type { ExportColumn } from '@/lib/tableExport';

export type MonthPdfLayout = 'halves' | 'one';
export const MONTH_PDF_LAYOUT_LABELS: Record<MonthPdfLayout, string> = {
  halves: 'Two halves (recommended)',
  one: 'All on one page',
};

/** How much room a day has on the page, which decides how much it can say. */
export type DayRoom = 'wide' | 'mid' | 'tiny';

export interface ClockingExportRow {
  name: string;
  jobTitle: string;
  cells: RegisterCell[];
  totals: PeriodSummary;
  noPunchDays: number;
}

export interface DayLine { text: string; bold: boolean }
export interface PrintedDay {
  lines: DayLine[];
  kind: CellKind;
  /** A clock-in or clock-out is missing: drawn with a dashed outline. */
  missing: boolean;
}

/** What one day prints as. */
export function printedDay(cell: RegisterCell, room: DayRoom, isHoliday: boolean): PrintedDay {
  const { kind, missing, v } = cell;
  const tiny = room === 'tiny';
  const one = (text: string, bold = false): PrintedDay => ({ lines: text ? [{ text, bold }] : [], kind, missing });
  if (!v) {
    if (kind === 'leave') return one(tiny ? 'L' : 'Leave', true);
    if (kind === 'noPunch') return one(tiny ? 'X' : 'No punch', true);
    if (kind === 'off') return one(isHoliday ? 'PH' : '-');
    return one(tiny ? '' : room === 'wide' ? 'Not uploaded' : 'Not yet');
  }
  const lines: DayLine[] = [
    { text: v.firstIn ?? (tiny ? 'in?' : 'no in'), bold: true },
    { text: v.lastOut ?? (tiny ? 'out?' : 'no out'), bold: !v.lastOut },
  ];
  if (v.late) {
    const m = v.lateMinutes;
    lines.push({
      text: room === 'wide' ? `${fmtMinutes(m)} late` : tiny && m < 60 ? String(m) : shortLate(m),
      bold: true,
    });
  }
  return { lines, kind, missing };
}

/** The pages of a month: which days go on each, and whether its totals do. */
export function monthParts(days: string[], layout: MonthPdfLayout): { days: string[]; withTotals: boolean; label: string }[] {
  if (layout === 'one') return [{ days, withTotals: true, label: '' }];
  const first = days.filter((d) => Number(d.slice(8)) <= 15);
  const rest = days.filter((d) => Number(d.slice(8)) > 15);
  const last = days[days.length - 1] ? Number(days[days.length - 1].slice(8)) : 31;
  return [
    { days: first, withTotals: false, label: '1 to 15' },
    { days: rest, withTotals: true, label: `16 to ${last}, with the month's totals` },
  ];
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "Mon 26 Oct", the same on every computer. */
export function exportDayLabel(date: string): string {
  const month = new Date(`${date}T12:00:00Z`).toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' }).slice(0, 3);
  return `${DOW[weekday(date)]} ${Number(date.slice(8))} ${month}`;
}

/** The note for a day with something to say besides its times. */
function dayNote(c: RegisterCell, holidayName: string | undefined): string {
  if (c.v) return c.v.missingIn ? 'No clock-in' : c.v.missingPunch ? 'No clock-out' : '';
  const word: Record<CellKind, string> = {
    leave: 'Leave', noPunch: 'No punch', notYet: 'Not uploaded', off: holidayName ?? 'Off',
    onTime: '', late15: '', late45: '', lateMore: '',
  };
  return word[c.kind];
}

/** One row per person: In, Out, Late and Note for each day, then the totals. */
export function clockingCsvColumns(
  days: string[],
  holidayName: (date: string) => string | undefined,
): ExportColumn<ClockingExportRow>[] {
  const dayCols = days.flatMap((d, i): ExportColumn<ClockingExportRow>[] => {
    const label = exportDayLabel(d);
    return [
      { header: `${label} In`, text: (r) => r.cells[i].v?.firstIn ?? '' },
      { header: `${label} Out`, text: (r) => r.cells[i].v?.lastOut ?? '' },
      { header: `${label} Late (min)`, text: (r) => String(r.cells[i].v?.lateMinutes || ''), raw: (r) => r.cells[i].v?.lateMinutes || null },
      { header: `${label} Note`, text: (r) => dayNote(r.cells[i], holidayName(d)) },
    ];
  });
  return [
    { header: 'Name', text: (r) => r.name },
    { header: 'Job title', text: (r) => r.jobTitle },
    ...dayCols,
    { header: 'Days late', text: (r) => String(r.totals.lateDays), raw: (r) => r.totals.lateDays },
    { header: 'Minutes late', text: (r) => String(r.totals.lateMinutes), raw: (r) => r.totals.lateMinutes },
    { header: 'Missed punches', text: (r) => String(r.totals.missingPunches), raw: (r) => r.totals.missingPunches },
    { header: 'No punch days', text: (r) => String(r.noPunchDays), raw: (r) => r.noPunchDays },
  ];
}
